import { ChangeDetectorRef, Component, inject, input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { ZiroFieldAbstractDirective } from '../../../../../forms/ziro-field-abstract.directive';
import { NaturalLanguageSingleOption } from '../../../../../shared/natural-language-editor/natural-language-editor-keyphrase/natural-language-editor-single/natural-language-editor-single.component';
import { NaturalLanguageEditorDoublePrimaryDropdownOption } from '../../../../../shared/natural-language-editor/natural-language-editor-keyphrase/natural-language-editor-double/natural-language-editor-double.component';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import {
  AssignedCallingDelegate,
  ForwardingTargetType,
  ForwardingType,
  TeamsCallingCallPickupGroup,
} from '../../../../../shared/models/generated/ziroModels';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep } from 'lodash';
import { NaturalLanguageEditorComponent } from '../../../../../shared/natural-language-editor/natural-language-editor.component';
import { UnansweredCallForwardingSettingsTargetType } from '../unanswered-call-forwarding-settings.component';
import { MicrosoftTeamsUserSearchResource } from '../../../../shared/resources/microsoft-teams-user-search.resource';

export enum ForwardingRulesFieldForwardingType {
  SIMULTANEOUS = 'SIMULTANEOUS',
  IMMEDIATE = 'IMMEDIATE',
}

interface ForwardingRulesFieldData {
  forwardingType: ForwardingRulesFieldForwardingType;
  forwardingTarget: {
    primary: UnansweredCallForwardingSettingsTargetType;
    secondary: string;
  };
}

export interface ForwardingRulesEntity {
  forwardingType: ForwardingType;
  forwardingTargetType: ForwardingTargetType;
  forwardingTarget: string;
}

@Component({
  selector: 'ziro-teams-calling-forwarding-rules-field',
  templateUrl: 'teams-calling-forwarding-rules-field.component.html',
  providers: [{ provide: ZiroFieldAbstractDirective, useExisting: TeamsCallingForwardingRulesFieldComponent }],
  standalone: false,
})
export class TeamsCallingForwardingRulesFieldComponent
  extends ZiroFieldAbstractDirective<ForwardingRulesEntity, ForwardingRulesFieldData>
  implements OnChanges
{
  private translateService = inject(TranslateService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private _microsoftTeamsUserSearchResource = inject(MicrosoftTeamsUserSearchResource);

  readonly userPrincipalName = input<string>();
  readonly userDisplayName = input<string>();
  readonly delegates = input<AssignedCallingDelegate[]>();
  readonly pickupGroups = input<TeamsCallingCallPickupGroup>();
  readonly tenantId = input<string>();

  @ViewChild(NaturalLanguageEditorComponent)
  naturalLanguageEditorComponent: NaturalLanguageEditorComponent<ForwardingRulesFieldData>;

  hasValidationError = false;

  ForwardingRulesFieldForwardingType = ForwardingRulesFieldForwardingType;

  forwardingTypeOptions: NaturalLanguageSingleOption[];
  simultaneousForwardingTargetTypeOptions: NaturalLanguageEditorDoublePrimaryDropdownOption[];
  immediateForwardingTargetTypeOptions: NaturalLanguageEditorDoublePrimaryDropdownOption[];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.pickupGroups?.currentValue) {
      this._setNaturalLanguageEditorConfig();
    }

    // trigger revalidation if the value of, "at least one delegate can receive calls," has changed.
    if (changes.delegates?.currentValue && changes.delegates?.previousValue) {
      const currentValidDelegates = changes.delegates.currentValue.some(
        (delegate: AssignedCallingDelegate) => delegate.receiveCallsEnabled,
      );
      const previousValidDelegates = changes.delegates.previousValue.some(
        (delegate: AssignedCallingDelegate) => delegate.receiveCallsEnabled,
      );
      if (currentValidDelegates !== previousValidDelegates) {
        this.fieldData = cloneDeep(this.fieldData);
      }
    }
  }

  toEntity = (fieldData: ForwardingRulesFieldData): ForwardingRulesEntity => {
    return {
      forwardingType: this._mapToEntityForwardingType(fieldData),
      forwardingTargetType: this._mapToEntityForwardingTargetType(fieldData),
      forwardingTarget: fieldData.forwardingTarget.secondary || '',
    };
  };

  toFieldData = (entity: ForwardingRulesEntity): ForwardingRulesFieldData => {
    return {
      forwardingType: this._mapToFieldDataForwardingType(entity),
      forwardingTarget: {
        primary: this._mapToFieldDataForwardingTargetType(entity),
        secondary: entity.forwardingTarget,
      },
    };
  };

  updateSelf(
    newFieldData: ForwardingRulesFieldData,
    markAsDirty = true,
    oldFieldData?: ForwardingRulesFieldData,
  ): void {
    const isForwardingTypeChanged = this.fieldData && this.fieldData.forwardingType !== newFieldData.forwardingType;
    super.updateSelf(newFieldData, markAsDirty, oldFieldData);
    this.changeDetectorRef.detectChanges(); // need to make sure the keyphrase components got their updates
    if (isForwardingTypeChanged) {
      newFieldData.forwardingTarget = {
        primary: this._defaultForwardingTargetType(newFieldData.forwardingType),
        secondary: '',
      };
      super.updateSelf(newFieldData, markAsDirty, oldFieldData);
    }
  }

  updateValidationState(hasValidationError: boolean): void {
    this.hasValidationError = hasValidationError;
  }

  private _mapToFieldDataForwardingType(entity: ForwardingRulesEntity): ForwardingRulesFieldForwardingType {
    switch (entity.forwardingType) {
      case ForwardingType.SIMULTANEOUS:
      case ForwardingType.NONE:
        return ForwardingRulesFieldForwardingType.SIMULTANEOUS;
      case ForwardingType.IMMEDIATE:
        return ForwardingRulesFieldForwardingType.IMMEDIATE;
    }
  }

  private _mapToFieldDataForwardingTargetType(
    entity: ForwardingRulesEntity,
  ): UnansweredCallForwardingSettingsTargetType {
    switch (entity.forwardingTargetType) {
      case ForwardingTargetType.NONE:
        return UnansweredCallForwardingSettingsTargetType.NONE;
      case ForwardingTargetType.VOICEMAIL:
        return UnansweredCallForwardingSettingsTargetType.VOICEMAIL;
      case ForwardingTargetType.DELEGATES:
        return UnansweredCallForwardingSettingsTargetType.DELEGATES;
      case ForwardingTargetType.CALL_PICKUP_GROUP:
        return UnansweredCallForwardingSettingsTargetType.CALL_PICKUP_GROUP;
      case ForwardingTargetType.SINGLE_TARGET:
        if (this.fieldData) {
          // fall back on this, there is no way to deduce whether it's a user or number if the field has already been touched
          return this.fieldData.forwardingTarget.primary;
        }
        return /^\+\d+$/.test(entity.forwardingTarget)
          ? UnansweredCallForwardingSettingsTargetType.SINGLE_TARGET_NUMBER
          : UnansweredCallForwardingSettingsTargetType.SINGLE_TARGET_USER;
    }
  }

  private _mapToEntityForwardingType(fieldData: ForwardingRulesFieldData): ForwardingType {
    switch (fieldData.forwardingType) {
      case ForwardingRulesFieldForwardingType.SIMULTANEOUS:
        return fieldData.forwardingTarget.primary === UnansweredCallForwardingSettingsTargetType.NONE
          ? ForwardingType.NONE
          : ForwardingType.SIMULTANEOUS;
      case ForwardingRulesFieldForwardingType.IMMEDIATE:
        return ForwardingType.IMMEDIATE;
    }
  }

  private _mapToEntityForwardingTargetType(fieldData: ForwardingRulesFieldData): ForwardingTargetType {
    switch (fieldData.forwardingTarget.primary) {
      case UnansweredCallForwardingSettingsTargetType.NONE:
        return ForwardingTargetType.NONE;
      case UnansweredCallForwardingSettingsTargetType.VOICEMAIL:
        return ForwardingTargetType.VOICEMAIL;
      case UnansweredCallForwardingSettingsTargetType.SINGLE_TARGET_USER:
      case UnansweredCallForwardingSettingsTargetType.SINGLE_TARGET_NUMBER:
        return ForwardingTargetType.SINGLE_TARGET;
      case UnansweredCallForwardingSettingsTargetType.DELEGATES:
        return ForwardingTargetType.DELEGATES;
      case UnansweredCallForwardingSettingsTargetType.CALL_PICKUP_GROUP:
        return ForwardingTargetType.CALL_PICKUP_GROUP;
    }
  }

  private _defaultForwardingTargetType(
    forwardingType: ForwardingRulesFieldForwardingType,
  ): UnansweredCallForwardingSettingsTargetType {
    switch (forwardingType) {
      case ForwardingRulesFieldForwardingType.SIMULTANEOUS:
        return UnansweredCallForwardingSettingsTargetType.NONE;
      case ForwardingRulesFieldForwardingType.IMMEDIATE:
        return UnansweredCallForwardingSettingsTargetType.VOICEMAIL;
    }
  }

  private _searchForUsers(searchTerm: string): Observable<string[]> {
    return this._microsoftTeamsUserSearchResource
      .search(this.tenantId(), searchTerm)
      .pipe(map((results) => results.map((user) => user.userPrincipalName)));
  }

  private _setNaturalLanguageEditorConfig(): void {
    this.forwardingTypeOptions = [
      {
        value: ForwardingRulesFieldForwardingType.SIMULTANEOUS,
        label: this.translateService.instant(
          'tkey;microsoft_360.view.teams.call_forward_settings.forwarding_rules.form.forward_type.simultaneous',
        ),
      },
      {
        value: ForwardingRulesFieldForwardingType.IMMEDIATE,
        label: this.translateService.instant(
          'tkey;microsoft_360.view.teams.call_forward_settings.forwarding_rules.form.forward_type.immediate',
        ),
      },
    ];
    this.simultaneousForwardingTargetTypeOptions = [
      {
        value: UnansweredCallForwardingSettingsTargetType.NONE,
        label: this.translateService.instant(
          'tkey;microsoft_360.view.teams.call_forward_settings.forwarding_rules.form.target_type.simultaneous.none',
        ),
        secondaryInputConfiguration: {
          type: 'none',
        },
      },
      {
        value: UnansweredCallForwardingSettingsTargetType.SINGLE_TARGET_USER,
        label: this.translateService.instant(
          'tkey;microsoft_360.view.teams.call_forward_settings.forwarding_rules.form.target_type.simultaneous.user',
        ),
        secondaryInputConfiguration: {
          type: 'search',
          label: this.translateService.instant(
            'tkey;microsoft_360.view.teams.call_forward_settings.target_type.user.label',
          ),
          placeholder: this.translateService.instant(
            'tkey;microsoft_360.view.teams.call_forward_settings.target_type.user.placeholder',
          ),
          searchFn: (searchTerm) => this._searchForUsers(searchTerm),
          validation: [
            {
              validator: (val) => !!val?.secondary,
              message: 'tkey;microsoft_360.view.teams.call_forward_settings.target_type.user.required_validator',
            },
            {
              validator: (val) => !val.secondary || val.secondary !== this.userPrincipalName(),
              message: 'tkey;microsoft_360.view.teams.call_forward_settings.target_type.user.not_self_validator',
            },
          ],
        },
      },
      {
        value: UnansweredCallForwardingSettingsTargetType.SINGLE_TARGET_NUMBER,
        label: this.translateService.instant(
          'tkey;microsoft_360.view.teams.call_forward_settings.forwarding_rules.form.target_type.simultaneous.number',
        ),
        secondaryInputConfiguration: {
          type: 'text',
          label: this.translateService.instant(
            'tkey;microsoft_360.view.teams.call_forward_settings.target_type.number.label',
          ),
          placeholder: this.translateService.instant(
            'tkey;microsoft_360.view.teams.call_forward_settings.target_type.number.placeholder',
          ),
          validation: [
            {
              validator: (val) => !!val?.secondary,
              message: 'tkey;microsoft_360.view.teams.call_forward_settings.target_type.number.required_validator',
            },
            {
              validator: (val) => /^\+?\d+$/.test(val.secondary),
              message: 'tkey;microsoft_360.view.teams.call_forward_settings.target_type.number.pattern_validator',
            },
          ],
        },
      },
      {
        value: UnansweredCallForwardingSettingsTargetType.DELEGATES,
        label: this.translateService.instant(
          'tkey;microsoft_360.view.teams.call_forward_settings.forwarding_rules.form.target_type.simultaneous.delegates',
        ),
        secondaryInputConfiguration: {
          type: 'none',
          validation: [
            {
              validator: () => this.delegates().some((delegate) => delegate.receiveCallsEnabled),
              message: 'tkey;microsoft_360.view.teams.call_forward_settings.target_type.delegates.delegates_validator',
            },
          ],
        },
      },
      {
        value: UnansweredCallForwardingSettingsTargetType.CALL_PICKUP_GROUP,
        label: this.translateService.instant(
          'tkey;microsoft_360.view.teams.call_forward_settings.forwarding_rules.form.target_type.simultaneous.call_pickup_group',
        ),
        disabled: this.pickupGroups().callPickupGroupMembers.length < 1,
        disabledTooltip: this.translateService.instant(
          'tkey;microsoft_360.view.teams.call_forward_settings.target_type.call_pickup_group.disabled_tooltip',
        ),
        secondaryInputConfiguration: {
          type: 'none',
        },
      },
    ];
    this.immediateForwardingTargetTypeOptions = [
      {
        value: UnansweredCallForwardingSettingsTargetType.VOICEMAIL,
        label: this.translateService.instant(
          'tkey;microsoft_360.view.teams.call_forward_settings.forwarding_rules.form.target_type.immediate.voicemail',
        ),
        secondaryInputConfiguration: {
          type: 'none',
        },
      },
      {
        value: UnansweredCallForwardingSettingsTargetType.SINGLE_TARGET_USER,
        label: this.translateService.instant(
          'tkey;microsoft_360.view.teams.call_forward_settings.forwarding_rules.form.target_type.immediate.user',
        ),
        secondaryInputConfiguration: {
          type: 'search',
          label: this.translateService.instant(
            'tkey;microsoft_360.view.teams.call_forward_settings.target_type.user.label',
          ),
          placeholder: this.translateService.instant(
            'tkey;microsoft_360.view.teams.call_forward_settings.target_type.user.placeholder',
          ),
          searchFn: (searchTerm) => this._searchForUsers(searchTerm),
          validation: [
            {
              validator: (val) => !!val?.secondary,
              message: 'tkey;microsoft_360.view.teams.call_forward_settings.target_type.user.required_validator',
            },
            {
              validator: (val) => !val.secondary || val.secondary !== this.userPrincipalName(),
              message: 'tkey;microsoft_360.view.teams.call_forward_settings.target_type.user.not_self_validator',
            },
          ],
        },
      },
      {
        value: UnansweredCallForwardingSettingsTargetType.SINGLE_TARGET_NUMBER,
        label: this.translateService.instant(
          'tkey;microsoft_360.view.teams.call_forward_settings.forwarding_rules.form.target_type.immediate.number',
        ),
        secondaryInputConfiguration: {
          type: 'text',
          label: this.translateService.instant(
            'tkey;microsoft_360.view.teams.call_forward_settings.target_type.number.label',
          ),
          placeholder: this.translateService.instant(
            'tkey;microsoft_360.view.teams.call_forward_settings.target_type.number.placeholder',
          ),
          validation: [
            {
              validator: (val) => !!val?.secondary,
              message: 'tkey;microsoft_360.view.teams.call_forward_settings.target_type.number.required_validator',
            },
            {
              validator: (val) => /^\+?\d+$/.test(val.secondary),
              message: 'tkey;microsoft_360.view.teams.call_forward_settings.target_type.number.pattern_validator',
            },
          ],
        },
      },
      {
        value: UnansweredCallForwardingSettingsTargetType.DELEGATES,
        label: this.translateService.instant(
          'tkey;microsoft_360.view.teams.call_forward_settings.forwarding_rules.form.target_type.immediate.delegates',
        ),
        secondaryInputConfiguration: {
          type: 'none',
          validation: [
            {
              validator: () => this.delegates().some((delegate) => delegate.receiveCallsEnabled),
              message: 'tkey;microsoft_360.view.teams.call_forward_settings.target_type.delegates.delegates_validator',
            },
          ],
        },
      },
      {
        value: UnansweredCallForwardingSettingsTargetType.CALL_PICKUP_GROUP,
        label: this.translateService.instant(
          'tkey;microsoft_360.view.teams.call_forward_settings.forwarding_rules.form.target_type.immediate.call_pickup_group',
        ),
        disabled: this.pickupGroups().callPickupGroupMembers.length < 1,
        disabledTooltip: this.translateService.instant(
          'tkey;microsoft_360.view.teams.call_forward_settings.target_type.call_pickup_group.disabled_tooltip',
        ),
        secondaryInputConfiguration: {
          type: 'none',
        },
      },
    ];
  }
}
